import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSmartBack } from '../../shared/navigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import TableData from '../../shared/components/ui/TableData';
import Modal from '../../shared/components/ui/Modal';
import { EditUser } from '../components/forms/EditUser';

import company_image from '../../shared/assets/png/company_image.png';
import {
  editPermission,
  getUserById,
  restoreUserAccess,
  restrictUserAccess,
  updateUserById
} from '../services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const UserDetails = () => {
  const currentUserId = Cookies.get('userId');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRestricting, setIsRestricting] = useState(false);
  const [isAccessConfirmOpen, setIsAccessConfirmOpen] = useState(false);
  const { id: userId } = useParams();
  const [isOpenEditUser, setIsOpenEditUser] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [changedPermissions, setChangedPermissions] = useState({});
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/users' });

  const { data: user, refetch: refetchUser } = useQuery({
    queryKey: ['userFull', userId],
    queryFn: () => getUserById(userId, { full: true }),
    enabled: !!userId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('User Details Viewed', {
      user_id: currentUserId,
      timestamp: new Date().toISOString()
    });
  }, [currentUserId]);

  useEffect(() => {
    // Filter permissions by vertical rules and existing is_visible behavior
    const all = user?.userResourceAccess || [];
    const vertical = (user?.designation?.vertical || '').toLowerCase();

    // Groups to include by vertical
    const byVertical = {
      accounts: new Set(['Invoice Management']),
      marketing: new Set(['Marketing Management']),
      inventory: new Set([
        'Requirement Management',
        'Quotation Management',
        'Category Management',
        'Inventory Management'
      ]),
      operations: new Set(['Task Management', 'Requirement Management']),
      sales: new Set(['Lead Management', 'Deal Management'])
      // management handled separately below
    };

    const isPaperwork = (name) => name?.toLowerCase()?.includes('paperwork');

    let filtered = [];
    if (vertical === 'management') {
      // Show everything except Common and Paperwork, and only those currently visible
      filtered = all.filter(
        (p) =>
          p.is_visible === true && p.permission_name !== 'Common' && !isPaperwork(p.permission_name)
      );
    } else if (byVertical[vertical]) {
      const allowed = byVertical[vertical];
      filtered = all.filter((p) => p.is_visible === true && allowed.has(p.permission_name));
    } else {
      // Default: preserve existing behavior (only visible), but hide Common and Paperwork as a sensible default
      filtered = all.filter(
        (p) =>
          p.is_visible === true && p.permission_name !== 'Common' && !isPaperwork(p.permission_name)
      );
    }

    setPermissions(filtered);
    setChangedPermissions({});
    refetchUser();
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: (data) => updateUserById(userId, data),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('User updated successfully');
      setIsOpenEditUser(false);
      refetchUser();
      queryClient.invalidateQueries({ queryKey: ['userLite', userId] });
      queryClient.invalidateQueries({ queryKey: ['userFull', userId] });
    },
    onError: (err) => {
      setIsSubmitting(false);
      const errorMessage = err.response?.data?.message || 'Failed to update user';
      toast.error(err?.response?.data?.message || err?.message || errorMessage);
    }
  });

  const accessMutation = useMutation({
    mutationFn: () =>
      user?.is_blocked ? restoreUserAccess(userId) : restrictUserAccess(userId),
    onMutate: () => {
      setIsRestricting(true);
    },
    onSuccess: (data) => {
      setIsRestricting(false);
      setIsAccessConfirmOpen(false);
      const wasRestricted = user?.is_blocked === true;
      const leadCount = data?.leads_still_assigned;
      if (wasRestricted) {
        toast.success('User access restored');
      } else if (typeof leadCount === 'number') {
        toast.success(
          `User restricted. ${leadCount} lead${leadCount === 1 ? '' : 's'} still assigned.`
        );
      } else {
        toast.success('User access restricted');
      }
      queryClient.setQueryData(['userFull', userId], (old) =>
        old
          ? {
              ...old,
              is_blocked: data?.is_blocked ?? !wasRestricted,
              assigned_lead_count:
                typeof leadCount === 'number' ? leadCount : old.assigned_lead_count
            }
          : old
      );
      refetchUser();
      queryClient.invalidateQueries({ queryKey: ['userLite', userId] });
      queryClient.invalidateQueries({ queryKey: ['userFull', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      setIsRestricting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update access');
    }
  });

  const { mutate: handlePermission, isLoading: isLoadingPermission } = useMutation({
    mutationFn: (payload) => editPermission(payload),
    onSuccess: async () => {
      toast.success('User Permission is given successfully');
      setIsEditing(!isEditing);
      setChangedPermissions({});
      await refetchUser(); // UI data update
      queryClient.invalidateQueries({ queryKey: ['userLite', userId] });
      queryClient.invalidateQueries({ queryKey: ['userFull', userId] });
    },
    onError: (err) => {
      console.error('Error editing new project');
      const errorMessage = err.response?.data?.message || 'Failed to update permissions';
      toast.error(err?.response?.data?.message || err?.message || errorMessage);
    }
  });

  const handlePermissionChange = (index, action, value) => {
    const updatedPermission = {
      ...permissions[index],
      [action]: value,
      user_id: userId
    };

    setPermissions((prev) => {
      const newPermissions = [...prev];
      newPermissions[index][action] = value;
      return newPermissions;
    });

    setChangedPermissions((prev) => ({
      ...prev,
      [permissions[index].permission_name]: updatedPermission
    }));
  };

  const handleSubmit = () => {
    const formattedPermissions = Object.values(changedPermissions)?.map((perm) => ({
      resource_name: perm.resource_name,
      permission_name: perm.permission_name,
      user_id: userId,
      can_view: perm.can_view,
      can_create: perm.can_create,
      can_update: perm.can_update,
      can_delete: perm.can_delete
    }));

    console.log('Submitting permissions:', formattedPermissions);

    handlePermission(formattedPermissions);
  };

  const isRestricted = user?.is_blocked === true;
  const assignedLeadCount =
    typeof user?.assigned_lead_count === 'number' ? user.assigned_lead_count : null;
  const isViewingSelf = String(currentUserId) === String(userId);

  const headerData = [
    { name: 'Phone Number', value: user?.phone_number },
    { name: 'Email Address', value: user?.email },
    { name: 'Vertical', value: user?.designation?.vertical ?? user?.vertical },
    {
      name: 'Designation Level',
      value:
        user?.level === 'Low'
          ? 'Executive Level'
          : user?.level === 'Medium'
            ? 'Manager Level'
            : 'Admin Level'
    },
    { name: 'Designation', value: user?.designation?.name }
  ];

  // Sales Manager: vertical Sales + level Medium — build project -> sales executives for detail table
  const isSalesManager =
    (user?.vertical === 'Sales' || user?.designation?.vertical === 'Sales') && user?.level === 'Medium';
  const salesExecutivesByProject = (() => {
    if (!isSalesManager || !user) return [];
    const projectIdToName = {};
    (user?.userProjectMapping || []).forEach((m) => {
      const project = m?.project ?? m;
      const id = project?.id ?? m?.project_id;
      if (id != null) projectIdToName[id] = project?.displayName || project?.name || `Project ${id}`;
    });
    const byProject = {};
    (user?.manager || []).forEach((entry) => {
      const projectId = entry?.project_id;
      const name = entry?.user?.name;
      if (projectId == null || !name) return;
      const projectNameFromEntry = entry?.project?.name ?? entry?.project?.displayName;
      if (!byProject[projectId]) {
        byProject[projectId] = {
          name: projectNameFromEntry ?? projectIdToName[projectId] ?? `Project ${projectId}`,
          userIds: new Set(),
          names: []
        };
      }
      if (byProject[projectId].userIds.has(entry.user_id)) return;
      byProject[projectId].userIds.add(entry.user_id);
      byProject[projectId].names.push(name);
    });
    return Object.entries(byProject);
  })();

  return (
    <>
      {isOpenEditUser && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name="Edit user"
          onClose={() => setIsOpenEditUser(false)}>
          <EditUser
            isSubmitting={isSubmitting}
            onSubmit={updateMutation.mutate}
            userId={userId}
            onClose={() => setIsOpenEditUser(false)}
          />
        </Modal>
      )}
      {isAccessConfirmOpen && (
        <Modal
          isDirty={false}
          confirmOnClose={false}
          width={45}
          name={isRestricted ? 'Restore access' : 'Restrict access'}
          onClose={() => setIsAccessConfirmOpen(false)}>
          <div className="flex flex-col gap-4">
            <p className="text-gray-800">
              {isRestricted
                ? `Restore login access for ${user?.name || 'this user'}?`
                : `Restrict ${user?.name || 'this user'} from logging in?`}
            </p>
            {user?.email && <p className="text-sm text-gray-500">{user.email}</p>}
            {isRestricted ? (
              <p className="text-sm text-gray-600">
                They will be able to log in again. Existing leads stay assigned.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                They will be logged out immediately.
                {assignedLeadCount != null
                  ? ` ${assignedLeadCount} lead${assignedLeadCount === 1 ? '' : 's'} stay assigned and visible to management.`
                  : ' Their leads stay assigned and visible to management.'}{' '}
                You can bulk-transfer them later from Sales.
              </p>
            )}
            <div className="flex justify-end items-center space-x-4 mt-2">
              <Button displayType="secondary" onClick={() => setIsAccessConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                displayType={isRestricted ? 'primary' : 'secondary-red'}
                onClick={() => accessMutation.mutate()}
                disabled={isRestricting}>
                {isRestricted ? 'Restore access' : 'Restrict access'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <Layout>
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `Users > ${user?.name}` }} backArrow={true} onBack={onBack} />
        </div>
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <GridWrapper width={100}>
            <div className="w-full flex flex-col justify-between items-center gap-8 mt-[6rem]">
              <div className="p-8 rounded-lg bg-white w-full shadow-md flex items-center justify-between gap-6 space-x-10">
                {user?.name ? (
                  <div className="bg-gray-300 w-[12rem] h-[12rem] rounded-full flex justify-center items-center">
                    <span className="text-[8rem] text-white bg-center bg-cover !object-cover !object-center">
                      {user.name[0]}
                    </span>
                  </div>
                ) : (
                  <img
                    src={company_image}
                    alt="company_image"
                    className="w-[12rem] bg-center bg-cover !object-cover !object-center"
                  />
                )}

                <div className="flex-1 flex flex-col gap-8">
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-semibold text-xl">{user?.name}</span>
                        {isRestricted && (
                          <span className="text-xs font-medium text-[#C2281B] bg-red-50 border border-red-200 rounded-full px-2.5 py-1">
                            Restricted
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400 text-sm">{user?.user_type}</span>
                    </div>
                  </div>

                  <div className="w-[100%] flex justify-between items-center gap-2 flex-wrap">
                    {headerData?.map((item, index) => (
                      <div className="flex flex-col gap-4 flex-shrink-0 min-w-[15%]" key={index}>
                        <span>{item.name}</span>
                        <span className="text-sm">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="w-full flex justify-end items-center space-x-4 pr-4 mt-4">
                    <Button displayType="secondary" onClick={() => setIsOpenEditUser(true)}>
                      Edit
                    </Button>
                    {!isViewingSelf && (
                      <Button
                        displayType={isRestricted ? 'secondary' : 'secondary-red'}
                        onClick={() => setIsAccessConfirmOpen(true)}
                        disabled={isRestricting}>
                        {isRestricted ? 'Restore access' : 'Restrict access'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 flex flex-col gap-8 justify-between items-start space-x-4 bg-white shadow-lg mt-10 rounded-lg">
              <div className="w-full flex flex-col gap-4">
                <div className="flex justify-between items-center w-full">
                  <span className="font-semibold">Permissions</span>

                  {!isEditing && (
                    <Button displayType="primary" onClick={() => setIsEditing(true)}>
                      Edit Permission
                    </Button>
                  )}
                </div>

                <div className="w-full bg-gray-100 flex flex-col p-4 ">
                  <div className="w-full flex justify-between items-center">
                    <TableData data="Permission" width={30} />
                    <TableData data="View Access" width={30} />
                    <TableData data="Create Access" width={30} />
                    <TableData data="Delete Access" width={30} />
                    <TableData data="Update Access" width={30} />
                  </div>
                </div>

                <div className="mt-[-1rem] overflow-scroll">
                  {permissions?.map((perm, index) => (
                    <div
                      key={index}
                      className="w-full bg-white border-b-[1px] border-gray-300 flex flex-col p-4">
                      <div className="w-full flex justify-between items-center">
                        <TableData
                          data={`${perm.permission_name} (${perm.resource_name})`}
                          width={30}
                        />
                        {['can_view', 'can_create', 'can_delete', 'can_update']?.map((action) => (
                          <label
                            key={action}
                            className="checkbox-container flex items-center w-[28%] space-x-3">
                            <input
                              type="checkbox"
                              className="form-checkbox h-5 w-5 text-blue-500"
                              disabled={!isEditing}
                              checked={perm[action]} // Ensure this is a boolean value
                              onChange={(e) =>
                                handlePermissionChange(index, action, e.target.checked)
                              }
                            />
                            <span className="ml-2">Enable</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full flex justify-end items-center space-x-4 pr-4 mt-4">
                {isEditing && (
                  <>
                    <Button displayType="secondary" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                    <Button
                      displayType="primary"
                      disabled={Object.keys(changedPermissions).length === 0}
                      onClick={handleSubmit}>
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Team table — only for Sales Managers (vertical Sales, level Medium) */}
            {isSalesManager && (
              <div className="p-8 flex flex-col gap-8 justify-between items-start bg-white shadow-lg mt-10 rounded-lg">
                <div className="w-full flex flex-col gap-4">
                  <span className="font-semibold">Team</span>
                  {salesExecutivesByProject.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-stone-200 rounded-lg overflow-hidden">
                        <thead className="bg-stone-100">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-gray-700">
                              Project Name
                            </th>
                            <th className="text-left px-4 py-3 font-medium text-gray-700">
                              Team
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesExecutivesByProject.map(([projectId, { name: projectName, names }]) => (
                            <tr
                              key={projectId}
                              className="border-b border-stone-100 hover:bg-stone-50">
                              <td className="px-4 py-3 text-gray-800">{projectName}</td>
                              <td className="px-4 py-3 text-gray-800">{names.join(', ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 py-4">No team members assigned to this manager.</p>
                  )}
                </div>
              </div>
            )}
          </GridWrapper>
        </div>
      </Layout>
    </>
  );
};

export default UserDetails;
